import { Feature } from "geojson";
import { useContext, useEffect, useMemo, useState } from "react";
//@ts-ignore
import Mazemap from "@itpi/mazemap";
import { MapContext } from "../context/map.context";
import { MapPin } from "../components/Map";

function getWindowDimensions() {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height,
  };
}

export default function useWindowDimensions() {
  const [windowDimensions, setWindowDimensions] = useState(
    getWindowDimensions()
  );

  useEffect(() => {
    function handleResize() {
      setWindowDimensions(getWindowDimensions());
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return windowDimensions;
}

export const useFocusOnFeature = (
  feature?: Feature,
  zoom = 17.5,
  options?: Omit<MapPin, "lng" | "lat">
) => {
  const { map, highlighter, reset, setPins, flyTo, fitBounds } =
    useContext(MapContext);
  const { width, height } = useWindowDimensions();

  const coords = useMemo(() => {
    if (feature) {
      const point = Mazemap.Util.Turf.center(feature);
      return point.geometry.coordinates as [number, number];
    }
  }, [feature]);

  useEffect(() => {
    if (feature && highlighter && coords) {
      const bounds = Mazemap.Util.Turf.bbox(feature);
      const [lng, lat] = coords;

      if (feature.geometry.type === "Polygon" && !zoom) {
        fitBounds(bounds, {
          padding: Math.min(width * 0.4, height * 0.4),
          pitch: 0,
          bearing: 0,
        });
      } else {
        flyTo({ center: { lng, lat }, zoom, pitch: 0 });
      }
      map.setZLevel(feature.properties?.zLevel);

      setPins([{ lat, lng, zLevel: feature.properties?.zLevel, ...options }]);

      if (highlighter && feature.geometry.type === "Polygon") {
        const { properties, ...geojson } = feature;

        highlighter.highlight({
          ...geojson,
          properties: {
            zLevel: 0,
            ...properties,
          },
        });
      }

      return reset;
    }
  }, [
    height,
    width,
    options,
    feature,
    map,
    highlighter,
    reset,
    zoom,
    setPins,
    coords,
    flyTo,
    fitBounds,
  ]);

  return { lng: coords?.[0], lat: coords?.[1] };
};
